<template>
  <Modal class="request-funds-modal" :closeBtn="!submitted" @close="closeModal">
    <template v-slot:header>
      <template v-if="!submitted">
        <p>Request Funds</p>
        <p class="header-desc">
          Submit a request for additional funds from your existing line of credit.
        </p>
      </template>
    </template>
    <dialogue-modal
      v-if="showDialogueModal === true"
      @cancel="showDialogueModal = false"
      @confirm="handleDialogueModal"
      :title="`Quit editing?`"
      :msg="`Any changes you\'ve made so far will not be saved`"
      :confirmButtonText="'Yes'"
      :cancelButtonText="'No'"
    />
    <Stepper v-if="activeStep < 4" :steps="steps" :activeStep="activeStep" />
    <template v-if="activeStep === 1">
      <SchoolInfo :loan="Loan" @close="cancelButton" @complete="schoolComplete" />
    </template>
    <template v-else-if="activeStep === 2">
      <FundingTerm @goback="goBack" @complete="fundingTermComplete" />
    </template>
    <template v-else-if="activeStep === 3">
      <AmountRequest :loan="Loan" @goback="goBack" @complete="amountRequestComplete" />
    </template>
    <template v-else-if="activeStep === 4">
      <ReviewRequest :referenceId="Loan?.referenceId" @goback="goBack" @complete="submitComplete" />
    </template>
    <template v-else>
      <div v-show="submitted" class="step-body success">
        <p class="step-title">Request Submitted!</p>
        <p class="step-desc">Your request has been submitted successfully!</p>
      </div>
    </template>
    <template v-slot:footer>
      <template v-if="submitted">
        <div class="step-btns">
          <button type="button" class="btn btn-secondary" @click.prevent="closeDrawModal">
            Close
          </button>
        </div>
      </template>
    </template>
  </Modal>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useStore } from "vuex";
import { $api } from "@/services/api";
import Modal from "@/components/shared/Modal.vue";
import SchoolInfo from "@/components/request-funds/SchoolInfo.vue";
import FundingTerm from "@/components/request-funds/FundingTerm.vue";
import AmountRequest from "@/components/request-funds/AmountRequest.vue";
import ReviewRequest from "@/components/request-funds/ReviewRequest.vue";
import Stepper from "@/components/shared/stepper/Stepper.vue";
import { mapGetters } from "vuex";
import type { LoanVM } from "@/models/opal";
import type { PropType } from "vue";
import DialogueModal from "../modals/DialogueModal.vue";

export default defineComponent({
  emits: ["complete", "previous", "getLoanDetails", "close"],
  setup(props, { emit }) {
    const store = useStore();
    const showDialogueModal = ref<boolean>(false);

    function openDialogueModal() {
      showDialogueModal.value = true;
    }

    function handleDialogueModal() {
      store.commit("fundsModule/resetAllStates");
      emit("getLoanDetails");
      emit("close");
    }
    return { store, showDialogueModal, openDialogueModal, handleDialogueModal };
  },
  props: {
    loan: {
      type: Object as PropType<LoanVM>,
      required: true,
      default: {} as any,
    },
  },
  components: {
    Modal,
    Stepper,
    SchoolInfo,
    FundingTerm,
    AmountRequest,
    ReviewRequest,
    DialogueModal,
  },
  data: () => ({
    loading: true,
    submitted: false,
    error: false,
    activeStep: 1,
    steps: [
      {
        title: "School Information",
      },
      {
        title: "Funding Term",
      },
      {
        title: "Amount Requested",
      },
    ],
    reviewConfirmation: false,
  }),
  computed: {
    ...mapGetters("oidcStore", ["oidcUser"]),
    ...mapGetters("fundsModule", ["Loan", "ApprovedLoanAmount", "LastRequestedAmount"]),
  },
  methods: {
    previous(event: any) {
      console.log("previous called");
      this.$emit("previous", event);
    },
    complete(event: any) {
      console.log("complete called");
      this.$emit("complete", event);
    },
    closeModal() {
      if (!this.submitted) {
        this.openDialogueModal();
      } else {
        this.store.commit("fundsModule/resetAllStates");
        this.$emit("close");
      }
    },
    amountRequestComplete(event: any) {
      this.activeStep = event.step;
    },
    fundingTermComplete(event: any) {
      this.activeStep = event.step;
    },
    schoolComplete(event: any) {
      this.activeStep = event.step;
    },
    submitComplete() {
      this.activeStep++;
      this.submitted = true;
      this.$emit("getLoanDetails");
    },
    goToReview() {
      this.activeStep = 4;
    },
    goBack() {
      if (this.activeStep === 4) {
        this.reviewConfirmation = false;
      }

      this.activeStep -= 1;
    },
    cancelButton() {
      // close the modal
      this.$emit("close");
    },
    closeDrawModal() {
      this.$emit("close", {
        refresh: true,
      });
    },
  },
});
</script>

<style>
.request-funds-modal .step-body {
  padding: 0 1.5rem;
  min-height: 250px;
  text-align: center;
}

.request-funds-modal .step-body.success {
  margin-top: 3rem;
}

.request-funds-modal .step-body p {
  margin-bottom: 0;
}

.request-funds-modal .step-body .step-title {
  font-size: 1.2rem;
  font-weight: 700;
}

.request-funds-modal .footer-wrapper {
  display: grid;
  width: 100%;
  text-align: center;
}

.request-funds-modal .footer-wrapper a {
  width: fit-content;
  text-decoration: underline;
  padding: 10px 0;
  margin: 0 auto;
}

.request-funds-modal .footer-wrapper a:hover {
  cursor: pointer;
}

.request-funds-modal .step-btns {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 75px;
  width: 100%;
}

.request-funds-modal .step-btns button {
  min-width: 110px;
  height: 58px;
  font-size: 1.188rem;
  font-weight: 600px;
}

.request-funds-modal .step-btns button:not(:first-child) {
  margin-left: 10px;
}

.request-funds-modal .modal-header .modal-title p {
  margin-bottom: 0;
  line-height: 1.5;
}

.request-funds-modal .modal-header .modal-title .header-desc {
  font-size: 0.8rem;
  color: gray;
}

.request-funds-modal .body-content {
  text-align: start;
  margin: 15px 0;
}

::placeholder {
  /* ... */
  text-align: center;
  margin: 15px 0;
}

.request-funds-modal .step-body .submit-error,
.request-funds-modal .body-content .form-group,
.request-funds-modal .body-content .submit-error {
  padding: 10px 1%;
  margin: 10px auto;
  border: 1px solid lightgray;
  border-radius: 0.6rem;
  max-width: 85%;
}

.request-funds-modal .body-content .form-group > label {
  font-weight: 700;
}

.request-funds-modal .body-content .form-group .form-check {
  padding-left: 0;
  max-width: 85%;
  margin: 0 auto;
}

.request-funds-modal .step-body .submit-error,
.request-funds-modal .body-content .submit-error {
  border-color: red;
  color: red;
  font-weight: 700;
  margin-top: 30px;
}

.request-funds-modal .step-body .submit-error ul,
.request-funds-modal .body-content .submit-error ul {
  margin-bottom: 0;
  list-style: none;
}

.request-funds-modal .form-inline {
  display: flex;
  justify-content: space-between;
}

.request-funds-modal .form-inline select {
  width: 48%;
}

.request-funds-modal .values-group {
  display: flex;
  justify-content: space-around;
  width: 95% !important;
}

.request-funds-modal .values-group .value-display {
  text-align: center;
}

.request-funds-modal .values-group .value-display label {
  font-weight: 700;
  margin-bottom: 0;
}

.request-funds-modal .values-group .value-display .value {
  padding-top: 1rem;
}

.request-funds-modal .values-group .value-display .value:last-child {
  padding-bottom: 0.5rem;
}

.request-funds-modal .review-content .form-check {
  text-align: center;
}

.request-funds-modal .invalid-feedback {
  display: contents !important;
}

/* Funding Term CSS */
.request-funds-modal .form-group.graduation-date-group label,
.request-funds-modal .form-group.enrollment-term-group label,
.request-funds-modal .form-group.enrollment-status-group label {
  margin: 0;
  font-weight: 700;
}

.request-funds-modal .form-group.graduation-date-group .form-check {
  margin-bottom: 10px;
}

/* Review Request CSS */
.request-funds-modal .form-group.table-container .value-display label {
  display: none;
}

.request-funds-modal .form-group.table-container .value-display .value {
  margin-top: 0;
}

.request-funds-modal .form-group.table-container table {
  text-align: center;
  width: 100%;
  border-collapse: collapse;
}

.request-funds-modal .form-group.table-container table th {
  padding-bottom: 0;
}

.request-funds-modal .form-group.table-container table tbody td {
  padding: 12px;
}

.request-funds-modal .form-group.table-container table tbody tr:first-child td {
  padding: 0 12px; /* Remove padding only for the very first row of cells inside tbody */
}

.request-funds-modal .form-group.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

@media (max-width: 576px) {
  .request-funds-modal .step-body .submit-error,
  .request-funds-modal .body-content .form-group,
  .request-funds-modal .body-content .submit-error,
  .request-funds-modal .body-content .form-group .form-check,
  .request-funds-modal .form-group.graduation-date-group,
  .request-funds-modal .form-group.enrollment-term-group,
  .request-funds-modal .form-group.enrollment-status-group {
    max-width: 100%;
  }
  .request-funds-modal .form-group.graduation-date-group,
  .request-funds-modal .form-group.enrollment-term-group,
  .request-funds-modal .form-group.enrollment-status-group,
  .request-funds-modal .form-group.school-selection,
  .request-funds-modal .form-group.requested-amount {
    border: none;
    padding: 0;
    margin: 0 2.5px;
  }
  .request-funds-modal .form-group.enrollment-term-group,
  .request-funds-modal .form-group.enrollment-status-group {
    margin-top: 10px;
  }

  .request-funds-modal .values-group {
    flex-direction: column;
  }

  .request-funds-modal.modal-body:has(.table-container) {
    max-height: 75vh;
    max-width: 97vw;
  }

  .request-funds-modal .form-group.table-container table th,
  .request-funds-modal .form-group.table-container table td,
  .request-funds-modal .form-group.table-container table tbody tr:first-child td {
    padding: 12px;
  }

  .request-funds-modal .form-group:has(.table-container) {
    max-width: 90%;
    margin: 0;
  }
}
</style>
